import { BasilFixture, BasilHttp, BranchesRead } from '../write/writeTypes';

/**
 * The read of record (spec sections 4.4 and 5.2).
 *
 * `this.state.branch[]` on the Story Branches page has NO `steps` under
 * `FEATURE_NODE_EDIT_SIDE_PANEL` — the page says so itself (`StoryBranches/index.js:3556-3559`) and
 * `revalidateAndOpenBranch` (`:1783-1855`) exists precisely to attach them. Basil therefore always
 * performs its own full read and never trusts `preloadedNodes` as data: they are a paint-fast hint,
 * good for the node count and the titles and nothing else.
 *
 * `GET /v1/stories/:id/branches/changes?since=` is NOT a fallback: the delta path never sets
 * `type: 'hub'` (`story_branch.js:1563-1583` against `:1414-1415`) and is a different shape, and a
 * step-only or answer-only edit-context write is invisible to it (AD1:44-46, AD2:11). If the full
 * read cannot be revalidated the read FAILS with a named error and nothing is decompiled (E13).
 */

export type EpisodeReadFailure =
  | 'request-failed'
  | 'maintenance-503'
  | 'no-branches'
  | 'missing-steps';

export interface EpisodeReadResult {
  ok: boolean;
  /** the immutable raw read: JSON round-tripped, so nothing downstream can alias it. */
  snapshot: BranchesRead | null;
  fixture: BasilFixture | null;
  failure: EpisodeReadFailure | null;
  message: string | null;
}

export function episodeBranchesPath(episodeId: number | string): string {
  return `/v1/stories/${episodeId}/branches`;
}

function freeze<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Read the episode in full and prove it carries steps.
 *
 * @param http the page's axios instance (or a mock: no test ever issues a real request)
 * @param episodeId `story.id` from the page. `fetchStoryBranchesByEpisodeId` declares a number and
 *  the page passes `match.params.id`, a string; Basil passes the number it has.
 */
export async function readEpisode(http: BasilHttp, episodeId: number): Promise<EpisodeReadResult> {
  let status = 0;
  let body: BranchesRead | null = null;

  try {
    const response = await http.get<BranchesRead>(episodeBranchesPath(episodeId));

    status = typeof response.status === 'number' ? response.status : 0;
    body = (response.data || null) as BranchesRead | null;
  } catch (error) {
    const axiosError = error as { message?: string; response?: { status?: number } };

    return {
      ok: false,
      snapshot: null,
      fixture: null,
      failure: 'request-failed',
      message: axiosError.message || "Basil couldn't open this episode. Try reloading it.",
    };
  }

  // `components/api.js:60-72` RESOLVES a 503 with an empty body; it is a failure, not an empty read.
  if (status === 503) {
    return {
      ok: false,
      snapshot: null,
      fixture: null,
      failure: 'maintenance-503',
      message: "Basil can't reach this episode right now. Try again in a minute.",
    };
  }

  if (!body || !Array.isArray(body.branches)) {
    return {
      ok: false,
      snapshot: null,
      fixture: null,
      failure: 'no-branches',
      message: "Basil couldn't read the scenes in this episode. Try reloading it.",
    };
  }

  const withoutSteps = body.branches.filter((branch) => !Array.isArray(branch.steps));

  if (withoutSteps.length > 0) {
    return {
      ok: false,
      snapshot: null,
      fixture: null,
      failure: 'missing-steps',
      message: "Part of this episode didn't come through. Reload it before editing.",
    };
  }

  const snapshot = freeze<BranchesRead>({
    success: body.success,
    branches: body.branches,
    serverTime: body.serverTime,
  });

  return {
    ok: true,
    snapshot,
    fixture: { branches: snapshot.branches },
    failure: null,
    message: null,
  };
}
